import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Label } from './Label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './Tooltip';
import { cn } from '../../lib/utils';

interface SelectInputProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string; disabled?: boolean }[];
  tooltip?: string;
  className?: string;
  placeholder?: string;
}

export const SelectInput: React.FC<SelectInputProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  tooltip,
  className,
  placeholder = "Select an option..."
}) => {
  const handleValueChange = (newValue: string) => {
    // CRITICAL FIX: Don't allow clearing the value if options are still loading
    // This prevents Radix Select from resetting to empty when options aren't available yet
    if (!newValue && value && options.length === 0) {
      return;
    }
    
    // Create a synthetic event to match the expected interface
    const syntheticEvent = {
      target: {
        name,
        value: newValue,
      }
    } as React.ChangeEvent<HTMLSelectElement>;
    
    onChange(syntheticEvent);
  };

  // CRITICAL FIX: If value exists but not in options (because they're still loading),
  // temporarily add it to the options list to prevent Select from resetting it
  const safeOptions = React.useMemo(() => {
    if (!value || options.some(opt => opt.value === value)) {
      return options;
    }
    // Value exists but not in options - add it temporarily
    return [{ value, label: value, disabled: false }, ...options];
  }, [value, options]);

  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={name} className="text-xs font-medium text-gray-600">
        {label}
      </Label>
      <Select value={value} onValueChange={handleValueChange}>
        <SelectTrigger id={name}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {safeOptions.map((option) => (
            <SelectItem 
              key={option.value} 
              value={option.value} 
              disabled={option.disabled}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};